import React, { useEffect, useState } from 'react';
import { StudentAppLayout } from '../../components/StudentAppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Loader2 } from 'lucide-react';
import { useLazyQuery, useEvent } from '@soap/modules';
import { useFormatDisplayDate } from '../../../utils/useFormatDisplayDate';

interface CompletedAssessmentDto {
  e266_AssessmentTitle: string;
  e266_CompletedAt?: string;
  e266_AssessmentResultState?: {
    key?: string;
    value?: string;
  };
  e266_Score?: number;
}

export function CompletedAssessmentsPage() {
  const formatDate = useFormatDisplayDate();
  const [completedAssessments, setCompletedAssessments] = useState<CompletedAssessmentDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [studentId, setStudentId] = useState<string | null>(null);
  
  // Get user profile to extract StudentId
  const [getUserProfile] = useLazyQuery("C166v1_GetUserProfile");
  const [getCompletedAssessments] = useLazyQuery("C278v1_GetCompletedStudentAssessments");

  useEvent({
    eventName: "E138v1_GotUserProfile",
    onEventReceived: (e138: { e138_StudentId?: string }) => {
      if (e138.e138_StudentId) {
        setStudentId(e138.e138_StudentId);
        getCompletedAssessments({ C278_StudentId: e138.e138_StudentId });
      } else {
        // Student user doesn't have StudentId linked, show empty state
        setStudentId(null);
        setCompletedAssessments([]);
        setLoading(false);
      }
    }
  });

  useEvent({
    eventName: "E266v1_GotCompletedStudentAssessments",
    onEventReceived: (event: { e266_CompletedAssessments: CompletedAssessmentDto[] }) => {
      setCompletedAssessments(event.e266_CompletedAssessments || []);
      setLoading(false);
    }
  });

  // Listen for assessment completed event to refresh the list
  useEvent({
    eventName: "E174v1_AssessmentCompleted",
    onEventReceived: () => {
      // Refresh completed assessments when a new assessment is completed
      if (studentId) {
        getCompletedAssessments({ C278_StudentId: studentId });
      }
    }
  });

  // Listen for assessment approved event to refresh the list (updates state and score)
  useEvent({
    eventName: "E180v1_AssessmentApproved",
    onEventReceived: () => {
      // Refresh completed assessments when an assessment is approved to update state and score
      if (studentId) {
        getCompletedAssessments({ C278_StudentId: studentId });
      }
    }
  });

  // Listen for assessment results deleted event to refresh the list
  useEvent({
    eventName: "E270v1_AssessmentResultsDeleted",
    onEventReceived: () => {
      // Refresh completed assessments when assessment results are deleted
      if (studentId) {
        getCompletedAssessments({ C278_StudentId: studentId });
      }
    }
  });

  // Listen for unit deleted event to refresh the list
  useEvent({
    eventName: "E255v1_UnitDeleted",
    onEventReceived: () => {
      // Refresh completed assessments when a unit with an assessment is deleted
      if (studentId) {
        getCompletedAssessments({ C278_StudentId: studentId });
      }
    }
  });

  useEffect(() => {
    setLoading(true);
    getUserProfile();
  }, [getUserProfile]);

  const getStateLabel = (state?: { key?: string; value?: string }) => {
    if (!state?.key) return 'N/A';
    switch (state.key.toLowerCase()) {
      case 'needs-marking':
        return 'Needs Marking';
      case 'teacher-approved':
        return 'Approved';
      default:
        return 'N/A';
    }
  };

  const getStateColor = (state?: { key?: string; value?: string }) => {
    if (!state?.key) return 'bg-gray-100 text-gray-800';
    switch (state.key.toLowerCase()) {
      case 'needs-marking':
        return 'bg-orange-100 text-orange-800';
      case 'teacher-approved':
        return 'bg-confirm-action/15 text-confirm-action-dark';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <StudentAppLayout>
        <div className="p-6 flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-action-blue" />
        </div>
      </StudentAppLayout>
    );
  }

  return (
    <StudentAppLayout>
      <div className="p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Completed Assessments</h1>
          <p className="mt-2 text-gray-600">View your completed assessment results</p>
        </div>

        {completedAssessments.length > 0 ? (
          <>
            {/* Mobile Card Layout */}
            <div className="sm:hidden space-y-4">
              {completedAssessments.map((assessment, index) => (
                <Card key={index}>
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div>
                        <h3 className="font-semibold text-gray-900">{assessment.e266_AssessmentTitle}</h3>
                      </div>
                      <div className="flex flex-col space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Date Completed:</span>
                          <span className="text-gray-900">
                            {assessment.e266_CompletedAt
                              ? formatDate(assessment.e266_CompletedAt)
                              : 'N/A'}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">State:</span>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStateColor(assessment.e266_AssessmentResultState)}`}>
                            {getStateLabel(assessment.e266_AssessmentResultState)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Score:</span>
                          <span className="text-gray-900 font-semibold">
                            {assessment.e266_Score != null 
                              ? `${assessment.e266_Score}%`
                              : 'N/A'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Tablet/Desktop Table Layout */}
            <Card className="hidden sm:block">
              <CardHeader>
                <CardTitle>Assessment Results</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Assessment Name</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Date Completed</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">State</th>
                        <th className="text-left py-3 px-4 font-semibold text-gray-900">Score</th>
                      </tr>
                    </thead>
                    <tbody>
                      {completedAssessments.map((assessment, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 text-gray-900">{assessment.e266_AssessmentTitle}</td>
                          <td className="py-3 px-4 text-gray-700">
                            {assessment.e266_CompletedAt
                              ? formatDate(assessment.e266_CompletedAt)
                              : 'N/A'}
                          </td>
                          <td className="py-3 px-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStateColor(assessment.e266_AssessmentResultState)}`}>
                              {getStateLabel(assessment.e266_AssessmentResultState)}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-gray-700">
                            {assessment.e266_Score != null 
                              ? `${assessment.e266_Score}%`
                              : 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card className="text-center py-12">
            <CardContent>
              <p className="text-gray-600">You haven't completed any assessments yet.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </StudentAppLayout>
  );
}

